import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./newsCarousel.module.css";
import NewsCard from "./NewsCard";
import CustomDot from "../../../components/Custom/CustomDot";
import NoNews from "../../../components/Error/NoNews/NoNews";

function NewsCarousel() {
    const [mergedNews, setMergedNews] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [itemsToShow, setItemsToShow] = useState(1);
    const [hovered, setHovered] = useState(false);

    const duration = 1.5;
    const scaledDown = 0.7;
    const opacityDown = 0.8;
    const scaledNormal = 1.0;
    const opacityNormal = 1.0;
    const interval = 5000;


    useEffect(() => {
        const responsive = [

            { min: 5 * 215, max: 4000, items: 5 },
            { min: 4 * 215, max: 5 * 215, items: 4 },
            { min: 3 * 185, max: 4 * 215, items: 3 },
            { min: 2 * 185, max: 3 * 185, items: 2 },
            { min: 0, max: 2 * 185, items: 1 },
        ];
        const updateItemsToShow = () => {
            const width = window.innerWidth;
            const match = responsive.find(r => width >= r.min && width < r.max);
            if (match) setItemsToShow(match.items);
        };

        updateItemsToShow();
        window.addEventListener("resize", updateItemsToShow);
        return () => window.removeEventListener("resize", updateItemsToShow);
    }, []);
    useEffect(() => {
        const fetchNews = async () => {
            try {
                const width = window.innerWidth;
                let totalNews = 5; // default

                if (width >= 2000) totalNews = 9;
                else if (width >= 1000) totalNews = 8;
                else if (width >= 800) totalNews = 7;
                else if (width >= 600) totalNews = 6;
                else if (width >= 400) totalNews = 5;
                else totalNews = 3;

                const res = await fetch(`/api/news/get_news_carousel_latest?limit=${totalNews}`);
                const news = await res.json();

                if (news.length > 0 && width >= 860) {
                    // visually pop the last one to the front for nicer flow
                    news.unshift(news.pop());
                }

                setMergedNews(news);
            } catch (err) {
                console.error("Error fetching news:", err);
            }
        };

        fetchNews();
    }, []);

    useEffect(() => {
        if (mergedNews.length === 0) return;
        if (hovered) return; // pause when hovered

        const timer = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % mergedNews.length);
        }, interval);

        return () => clearInterval(timer);
    }, [mergedNews, hovered]);

    if (mergedNews.length === 0) {
        return (
            <div className={styles.errorContainer}>
                <NoNews layout="horizontal" />
            </div>
        );
    }

    const getVisibleItems = () => {
        const visibleItems = [];
        for (let i = 0; i < itemsToShow; i++) {
            visibleItems.push(mergedNews[(currentIndex + i) % mergedNews.length]);
        }
        return visibleItems;
    };

    const visibleItems = getVisibleItems();

    return (
        <section className={styles.carouselContainer}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <div className={styles.carousel}>
                <AnimatePresence mode="popLayout">
                    {visibleItems.map((item, index) => {
                        const isFirst = index === 0;
                        const isLast = index === visibleItems.length - 1;

                        const isScaledDown = itemsToShow >= 4 && (isFirst || isLast);

                        const animationProps = {
                            initial: {

                                scale: isScaledDown ? scaledDown : scaledNormal,
                                opacity: 0,
                                zIndex: 0,
                            },
                            animate: {
                                scale: isScaledDown ? scaledDown : scaledNormal,
                                opacity: isScaledDown ? opacityDown : opacityNormal,
                                zIndex: 1,
                            },
                            exit: {

                                opacity: 0,
                                scale: scaledDown,
                                zIndex: 0
                            },
                            transition: { duration },
                        };

                        return (
                            <motion.div
                                layout
                                key={item.article_id}
                                className={styles.carouselItem}
                                {...animationProps}
                            >
                                <NewsCard data={item} />
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>
            <div className={styles.bottomRow}>
                <ul className={styles.customDotsContainer}>
                    {mergedNews.map((_, idx) => (
                        <CustomDot
                            key={idx}
                            active={idx === currentIndex % mergedNews.length}
                            onClick={() => setCurrentIndex(idx)}
                        />
                    ))}
                </ul>

                <a href="/news" className={styles.newsLink}>
                    Weitere Nachrichten hier...
                </a>
            </div>
        </section>
    );
}

export default NewsCarousel;
